import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Ban, Eye, AlertTriangle } from 'lucide-react';
import { liveApi, type LivePlayer } from '@/api/endpoints';
import { toast } from '@/store/toast';

export function WatchDialog({ player, onClose, onDone }: { player: LivePlayer; onClose: () => void; onDone: (hwid: string) => void; }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [severity, setSeverity] = useState(5);
  const [notes, setNotes] = useState('');

  async function submit() {
    setBusy(true);
    try {
      await liveApi.watch(player.hwid, { severity, notes });
      onDone(player.hwid);
    } catch (e: any) {
      toast.error(t('common.error'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-amber-600" /> {t('live.actions.watchTitle')}</DialogTitle>
          <DialogDescription>{t('live.actions.watchDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{t('live.table.nickname')}</Label>
            <div className="text-sm">
              {player.nickname || player.characterName || '(—)'}
              {player.userId ? <span className="text-muted-foreground"> · userId {player.userId}</span> : null}
            </div>
            <div className="font-mono text-xs text-muted-foreground break-all">{player.hwid}</div>
          </div>
          <div>
            <Label htmlFor="sev" className="text-xs">{t('live.actions.severity')}</Label>
            <Input id="sev" type="number" min={0} max={10} value={severity} onChange={e => setSeverity(Number(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="notes" className="text-xs">{t('live.actions.notes')}</Label>
            <Textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('live.actions.notesPlaceholder')} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={busy}>{busy ? t('live.actions.saving') : t('live.actions.markBtn')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BanDialog({ player, onClose, onDone }: { player: LivePlayer; onClose: () => void; onDone: (hwid: string) => void; }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [severity, setSeverity] = useState(9);
  const [confirm, setConfirm] = useState('');
  const [notes, setNotes] = useState('');

  // Palavra "BANIR" e fixa intencionalmente (codifica a acao de forma irreversivel
  // independente do idioma — todos os usuarios devem digitar exatamente isso)
  const canSubmit = confirm === 'BANIR' && !busy;

  async function submit() {
    setBusy(true);
    try {
      await liveApi.ban(player.hwid, { confirm: 'BANIR', severity, notes });
      onDone(player.hwid);
    } catch (e: any) {
      toast.error(t('live.actions.kickFailed'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Ban className="h-5 w-5" /> {t('live.actions.banTitle')}
          </DialogTitle>
          <DialogDescription>{t('live.actions.banDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 p-2 rounded border border-destructive/40 bg-destructive/5 text-xs">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <span>{t('live.actions.banWarn')}</span>
          </div>
          <div>
            <Label className="text-xs">{t('live.table.nickname')}</Label>
            <div className="text-sm">
              {player.nickname || player.characterName || '(—)'}
              {player.userId ? <span className="text-muted-foreground"> · userId {player.userId}</span> : null}
            </div>
            <div className="font-mono text-xs text-muted-foreground break-all">{player.hwid}</div>
            {player.ipAddress && <div className="text-xs text-muted-foreground mt-0.5">IP: {player.ipAddress}</div>}
          </div>
          <div>
            <Label htmlFor="bansev" className="text-xs">{t('live.actions.severity')}</Label>
            <Input id="bansev" type="number" min={0} max={10} value={severity} onChange={e => setSeverity(Number(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="bannotes" className="text-xs">{t('live.actions.reasonRequired')}</Label>
            <Textarea id="bannotes" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('live.actions.reasonPlaceholder')} rows={3} />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs">{t('live.actions.confirmType')}</Label>
            <Input id="confirm" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="BANIR" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={submit} disabled={!canSubmit}>{busy ? t('live.actions.banning') : t('live.actions.confirmBan')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
